import { useAuth } from '@/context/auth-context'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { DevLogin } from './dev-login'
import { GoogleSignInLegacy } from './google-signin-legacy'
import { toast } from '@/components/ui/use-toast'
import { PRIVATE_BETA_ENABLED } from '@/config/api'

// Declare the google global variable
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void
          renderButton: (element: HTMLElement, config: any) => void
          prompt: () => void
        }
      }
    }
  }
}

export function SignUp() {
  const { login, error, loading, user } = useAuth()
  const [initError, setInitError] = useState<string | null>(null)
  const [googleLoaded, setGoogleLoaded] = useState(false)
  const [searchParams] = useSearchParams()
  const referralCode = searchParams.get('ref')
  
  // Store referral code in session storage so it persists through auth flow
  useEffect(() => {
    if (referralCode) {
      sessionStorage.setItem('pagemind_referral_code', referralCode)
      console.log('Signup with referral code:', referralCode)
    }
  }, [referralCode])

  useEffect(() => {
    // Check if Google script is already loaded
    if (window.google?.accounts?.id) {
      setGoogleLoaded(true)
      initializeGoogleSignIn()
      return
    }
    
    // Load the Google Sign-In API script
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      setGoogleLoaded(true)
      initializeGoogleSignIn()
    }
    script.onerror = () => {
      setInitError('Failed to load Google authentication. Please try again later.')
    }
    document.body.appendChild(script)

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
    }
  }, [])

  const initializeGoogleSignIn = () => {
    if (window.google?.accounts?.id) {
      try {
        const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
        
        if (!googleClientId) {
          setInitError('Google client ID is not configured. Please check environment variables.')
          return
        }

        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        // Render the Google Sign-In button
        const buttonElement = document.getElementById('google-signin-button')
        if (buttonElement) {
          window.google.accounts.id.renderButton(buttonElement, {
            theme: 'outline',
            size: 'large',
            type: 'standard',
            text: 'signup_with',
            width: 250,
          })
        }
      } catch (error) {
        console.error('Google Sign-In initialization error:', error)
        setInitError(`Failed to initialize Google authentication: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    }
  }

  const handleCredentialResponse = async (response: any) => {
    console.log('Google credential response received for signup')
    if (response.credential) {
      // Store that this is a signup (not login)
      sessionStorage.setItem('pagemind_is_signup', 'true')
      await login(response.credential)
    }
  }

  // Process referral activation after successful signup
  useEffect(() => {
    const activateReferral = async () => {
      const storedReferralCode = sessionStorage.getItem('pagemind_referral_code')
      const isSignup = sessionStorage.getItem('pagemind_is_signup')
      
      if (user && storedReferralCode && isSignup === 'true') {
        try {
          // Get the user's token
          const token = localStorage.getItem('token')
          
          if (token) {
            // Call backend API to activate referral
            const response = await fetch('/api/referrals/activate', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ referralCode: storedReferralCode })
            })
            
            if (response.ok) {
              const data = await response.json()
              console.log('Referral activated:', data)
              
              // Show welcome message with bonus info
              toast({
                title: "Welcome to PageMind! 🎉",
                description: `Your account has been created and you've earned ${data.bonusConversations} bonus conversations! Let's get your website set up.`,
                duration: 5000,
              })
            } else {
              console.error('Failed to activate referral:', await response.text())
            }
          }
          
          // Clear the flags
          sessionStorage.removeItem('pagemind_referral_code')
          sessionStorage.removeItem('pagemind_is_signup')
        } catch (error) {
          console.error('Failed to activate referral:', error)
        }
      }
    }
    
    if (user && !loading) {
      activateReferral()
    }
  }, [user, loading])

  // Get location state to handle redirects after signup
  const location = useLocation()
  
  // If user is already authenticated, redirect appropriately
  if (user && !loading) {
    const isSignup = sessionStorage.getItem('pagemind_is_signup')
    const isWaitlistFlow = sessionStorage.getItem('pagemind_waitlist_flow')
    
    if (PRIVATE_BETA_ENABLED && isWaitlistFlow === 'true') {
      // In waitlist flow, redirect to add-site page
      sessionStorage.removeItem('pagemind_waitlist_flow')
      return <Navigate to="/add-site" replace />
    } else if (isSignup === 'true') {
      // For new signups, go to home page with onboarding flow
      return <Navigate to="/" state={{ showOnboarding: true, fromSignup: true }} replace />
    } else {
      // Existing user trying to access signup, redirect to home
      return <Navigate to="/" replace />
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="w-full border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <a href="https://www.pagemind.ai" className="flex items-center">
              <img src="/pagemind-logo.png" alt="PageMind" className="h-8 w-auto" />
            </a>
            <div className="hidden sm:flex sm:items-center sm:space-x-6">
              <a href="https://www.pagemind.ai/features" className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                Features
              </a>
              <a href="https://www.pagemind.ai/demo/aeo-impact" className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                Demo
              </a>
              <a href="/login" className="text-sm font-medium text-primary hover:underline">
                Login
              </a>
            </div>
          </div>
        </nav>
      </header>

      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-3xl font-bold">Join PageMind</h1>
            <p className="mt-2 text-muted-foreground">
              Create your free account and start engaging with your website visitors using AI.
            </p>
            {referralCode && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md dark:bg-green-900/20 dark:border-green-800">
                <p className="text-sm text-green-800 dark:text-green-200">
                  🎁 You've been invited! Sign up now to get bonus conversations.
                </p>
              </div>
            )}
            <div className="mt-4">
              <a
                href="https://www.pagemind.ai/demo/aeo-impact"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                See how it works
              </a>
            </div>
          </div>

        <div className="mt-8 space-y-4">
          {initError && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
              {initError}
            </div>
          )}
          
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div id="google-signin-button" className="flex justify-center"></div>
                
                {!googleLoaded && (
                  <GoogleSignInLegacy />
                )}
              </div>

              {/* Show dev login in development mode */}
              {import.meta.env.DEV && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Or continue with development mode
                      </span>
                    </div>
                  </div>
                  <DevLogin />
                </>
              )}
            </>
          )}
          
          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <a href="/login" className="font-medium text-primary hover:underline">
              Sign in
            </a>
          </div>
        </div>
        </div>
      </div>
    </div>
  )
}